import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Stack } from "@mui/material";
import type { TuFunction } from "@src/appV2/i18n/types";
import { SUPPORT_TELEPHONE, SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { FACILITY_HELP_SUPPORT_LINK } from "@src/appV2/HelpCenter/paths";
import { type SupportArticle } from "@src/appV2/HelpCenter/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useEffect, useRef } from "react";
import { Text } from "../components/Text";
import { HelpCenterActionCard } from "./components/HelpCenterActionCard";
import { HelpCenterHero } from "./components/HelpCenterHero";
import { HelpCenterKnowledgeBase } from "./components/HelpCenterKnowledgeBase";
import { HelpCenterResultsSurface } from "./components/HelpCenterResultsSurface";
import { HelpCenterSearchField } from "./components/HelpCenterSearchField";

import {
  HelpCenterSpecialistSection,
  type SpecialistCardData,
} from "./components/HelpCenterSpecialistSection";

import { HelpCenterSupportFooter } from "./components/HelpCenterSupportFooter";
import { ALL_SUPPORT_ARTICLES_CATEGORY, MAX_SUPPORT_ARTICLE_SUGGESTIONS } from "./constants";
import { getBackgroundBase } from "./themeHelpers";
import { type SupportArticleGroup } from "./types";
import { getSuggestedSupportArticles } from "./utils";

interface HelpCenterViewProps {
  amSpecialist?: SpecialistCardData;
  articleGroups: SupportArticleGroup[];
  categoryArticleGroups: SupportArticleGroup[];
  billingSpecialist?: SpecialistCardData;
  categories: string[];
  isError: boolean;
  isLoading: boolean;
  isMobile: boolean;
  isSuccess: boolean;
  searchQuery: string;
  selectedCategory: string;
  supportArticles: SupportArticle[];
  onSearchChange: (searchQuery: string) => void;
  onSelectCategory: (selectedCategory: string) => void;
}

function getResultsTitle(searchQuery: string, selectedCategory: string, tu: TuFunction) {
  if (searchQuery.length > 0) {
    return tu("Search results");
  }

  if (selectedCategory === ALL_SUPPORT_ARTICLES_CATEGORY) {
    return tu("FAQs");
  }

  return selectedCategory;
}

export function HelpCenterView(props: HelpCenterViewProps) {
  const { tu } = useTu("helpCenter");

  const {
    amSpecialist,
    articleGroups,
    categoryArticleGroups,
    billingSpecialist,
    categories,
    isError,
    isLoading,
    isMobile,
    isSuccess,
    searchQuery,
    selectedCategory,
    supportArticles,
    onSearchChange,
    onSelectCategory,
  } = props;

  const hasNoSupportArticles = isSuccess && supportArticles.length === 0;
  const hasNoSearchResults = isSuccess && supportArticles.length > 0 && articleGroups.length === 0;

  const hasNoCategoryResults =
    isSuccess && supportArticles.length > 0 && categoryArticleGroups.length === 0;

  const shouldShowGroupTitles = articleGroups.length > 1;

  const suggestedSupportArticles = getSuggestedSupportArticles({
    supportArticles,
    searchQuery,
    limit: MAX_SUPPORT_ARTICLE_SUGGESTIONS,
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const shouldShowSuggestions =
    normalizedSearchQuery.length > 0 &&
    suggestedSupportArticles.length > 0 &&
    !suggestedSupportArticles.some(
      (suggestedSupportArticle) =>
        suggestedSupportArticle.attributes.title.trim().toLowerCase() === normalizedSearchQuery
    );

  const resultsTitle = getResultsTitle(searchQuery, selectedCategory, tu);
  const isSearching = normalizedSearchQuery.length > 0;
  const isCategoryFiltered = selectedCategory !== ALL_SUPPORT_ARTICLES_CATEGORY;
  const displayCategories = categories.filter((c) => c !== ALL_SUPPORT_ARTICLES_CATEGORY);
  const articleCountByCategory = new Map<string, number>();

  for (const article of supportArticles) {
    const count = articleCountByCategory.get(article.attributes.category) ?? 0;
    articleCountByCategory.set(article.attributes.category, count + 1);
  }

  const searchResultsSurface = (
    <HelpCenterResultsSurface
      articleGroups={articleGroups}
      hasNoSearchResults={hasNoSearchResults}
      hasNoSupportArticles={hasNoSupportArticles}
      isError={isError}
      isLoading={isLoading}
      isMobile={isMobile}
      resultsTitle={resultsTitle}
      shouldShowGroupTitles={shouldShowGroupTitles}
    />
  );

  const categoryResultsSurface = (
    <HelpCenterResultsSurface
      articleGroups={categoryArticleGroups}
      hasNoSearchResults={hasNoCategoryResults}
      hasNoSupportArticles={hasNoSupportArticles}
      isError={isError}
      isLoading={isLoading}
      isMobile={isMobile}
      resultsTitle={selectedCategory}
      shouldShowGroupTitles={false}
    />
  );

  const categoryResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isMobile && isCategoryFiltered && categoryResultsRef.current) {
      categoryResultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedCategory, isMobile, isCategoryFiltered]);

  const searchField = (
    <HelpCenterSearchField
      isMobile={isMobile}
      searchQuery={searchQuery}
      shouldShowSuggestions={shouldShowSuggestions}
      suggestedSupportArticles={suggestedSupportArticles}
      onSearchChange={onSearchChange}
      onSelectCategory={onSelectCategory}
    />
  );

  return (
    <Stack
      sx={(theme) => ({
        height: isMobile ? "100%" : undefined,
        flex: 1,
        minHeight: 0,
        overflow: isMobile ? "hidden" : "visible",
        backgroundColor: getBackgroundBase(theme),
        borderRadius: isMobile ? undefined : theme.borderRadius?.large,
        boxShadow: isMobile ? undefined : theme.shadow?.card,
      })}
    >
      <Box
        sx={(theme) => ({
          height: isMobile ? "100%" : undefined,
          flex: 1,
          minHeight: 0,
          overflowY: isMobile ? "auto" : "visible",
          WebkitOverflowScrolling: isMobile ? "touch" : undefined,
          overscrollBehaviorY: isMobile ? "contain" : undefined,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          paddingBottom: isMobile
            ? `calc(${theme.size?.floatingNavigation?.height ?? "3.375rem"} + ${
                theme.size?.floatingNavigation?.margin ?? "15px"
              } + 3rem)`
            : theme.spacing(6),
        })}
      >
        <Stack
          spacing={isMobile ? 3 : 6}
          sx={(theme) => ({
            maxWidth: "64rem",
            marginX: "auto",
            width: "100%",
            paddingTop: theme.spacing(isMobile ? 2 : 6),
            paddingLeft: theme.spacing(isMobile ? 3 : 4),
            paddingRight: theme.spacing(isMobile ? 3 : 4),
          })}
        >
          <HelpCenterHero isMobile={isMobile}>{!isMobile ? searchField : null}</HelpCenterHero>

          {isMobile ? (
            <Box sx={{ position: "relative", zIndex: 2, marginTop: "-2rem" }}>{searchField}</Box>
          ) : null}

          {isSearching && searchResultsSurface}

          <Box
            sx={(theme) => ({
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: theme.spacing(2),
            })}
          >
            <HelpCenterActionCard
              description={
                <>
                  <Text semibold sx={(theme) => ({ color: theme.palette.text.primary })}>
                    {SUPPORT_TELEPHONE}
                  </Text>
                  {tu("Talk to support any time.")}
                </>
              }
              href={`tel:${SUPPORT_TELEPHONE}`}
              isMobile={isMobile}
              title={tu("Call 24/7 support")}
            />
            <HelpCenterActionCard
              description={tu("Send details and we'll follow up.")}
              href={SUPPORT_WEBFORM_URL}
              iconType="documents"
              isMobile={isMobile}
              rel="noopener noreferrer"
              target="_blank"
              title={tu("Submit a request")}
            />
            <HelpCenterActionCard
              description={tu("Open the full support library.")}
              href={FACILITY_HELP_SUPPORT_LINK}
              iconType="book"
              isMobile={isMobile}
              rel="noopener noreferrer"
              target="_blank"
              title={tu("Browse all articles")}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.HELP_CENTER_EXTERNAL_LINK_CLICKED);
              }}
            />
          </Box>

          <HelpCenterSpecialistSection
            amSpecialist={amSpecialist}
            billingSpecialist={billingSpecialist}
            isMobile={isMobile}
          />

          <HelpCenterKnowledgeBase
            articleCountByCategory={articleCountByCategory}
            displayCategories={displayCategories}
            isMobile={isMobile}
            selectedCategory={selectedCategory}
            onSelectCategory={onSelectCategory}
          />

          <div ref={categoryResultsRef}>{isCategoryFiltered && categoryResultsSurface}</div>

          <HelpCenterSupportFooter isMobile={isMobile} />
        </Stack>
      </Box>
    </Stack>
  );
}
